<template>
  <v2-container v-if="teams[$route.params.team].account">
    <v-delete-modal
      name="delete-projects-modal"
      :title="
        $t('modals.deleteProject.title', {
          name: $h.getNameFromProjectsByUid(currentDeleteUid, projects),
        })
      "
      :text="$t('modals.deleteProject.text')"
      :deleteHandler="
        () => {
          deleteProjectModalHandler(currentDeleteUid);
        }
      "
    >
    </v-delete-modal>
    <v-delete-modal
      name="delete-filterPresets-modal"
      :title="
        currentDeleteFilterPreset
          ? $h.formatFilterPresetName(currentDeleteFilterPreset, teams[$route.params.team].account.locale)
          : ''
      "
      :text="$t('modals.deleteFilterPreset.text')"
      :deleteHandler="
        () => {
          deleteFilterPresetModalHandler('projects', setProjectsFilters);
        }
      "
    >
    </v-delete-modal>
    <modal
      name="duplicate-project"
      class="envivo-modal"
      @closed="duplicateModalClosed"
      @before-open="$h.toggleHtmlScroll"
      @before-close="$h.toggleHtmlScroll"
      transition="pop-out"
      height="auto"
      adaptive
      :maxWidth="600"
      width="90%"
      scrollable
    >
      <div class="modal-wrapper">
        <div class="modal-title">{{ $t("actions.duplicateProject") }}</div>
        <div class="modal-content">
          <form @submit.prevent>
            <v-textfield
              :label="$t('labels.giveName')"
              v-model="duplicateTitle"
              v-validate="'required'"
              name="title"
            ></v-textfield>
            <label for="client">{{ $t("labels.selectMultipleClients") }}</label>
            <v2-select
              :placeholder="$t('placeholders.searchForClient')"
              v-model="duplicateClients"
              :options="clientOptions"
              :multiple="true"
              trackBy="uid"
              :maxHeight="150"
              optionLabel="last_name"
              id="client"
              name="client"
              v-validate="'required'"
              :error="errors.first('client')"
              :data-vv-as="$t('client.client')"
              :searchable="true"
              :internalSearch="false"
              :loading="isLoading"
              @searchChange="loadClients"
              :customLabel="setCustomLabel"
              :hideSelected="true"
              openDir="bottom"
            ></v2-select>
            <v-checkbox :label="$t('labels.duplicateWithFiles')" v-model="duplicateFiles"></v-checkbox>
            <div class="flex items-center mb-1 mt-8">
              <v2-icon
                style="margin-left: -2px; margin-right: 2px"
                :data="require('@icon/Icon=info.svg')"
                width="20"
                height="20"
              ></v2-icon>
              {{ $t("modals.duplicateProject.title") }}
            </div>
            <p>{{ $t("modals.duplicateProject.text") }}</p>
          </form>
        </div>
        <div class="modal-buttons">
          <a class="v1 simple" @click="$modal.hide('duplicate-project')">{{ $t("actions.cancel") }}</a>
          <v-button primary @click.native="duplicateThisProject">{{ $t("actions.duplicate") }}</v-button>
        </div>
      </div>
    </modal>
    <modal
      name="saveFilterPreset"
      class="envivo-modal"
      @before-open="$h.toggleHtmlScroll"
      @before-close="$h.toggleHtmlScroll"
      transition="pop-out"
      height="auto"
      adaptive
      :maxWidth="600"
      width="90%"
      scrollable
    >
      <div class="modal-wrapper">
        <div class="modal-title">{{ $t("titles.saveFilterPreset") }}</div>
        <div class="modal-content">
          <form @submit.prevent>
            <v-textfield
              :label="$t('labels.giveFilterPresetName')"
              v-model="filterPresetName"
              v-validate="'required'"
              name="title"
            ></v-textfield>
          </form>
        </div>
        <div class="modal-buttons">
          <a class="v1 simple" @click="$modal.hide('saveFilterPreset')">{{ $t("actions.cancel") }}</a>
          <v-button primary @click.native="saveFilterPreset('projects')">{{
            $t("actions.createFilterPreset")
          }}</v-button>
          <v-button
            primary
            @click.native="saveFilterPreset('projects', true)"
            :active="
              !!(
                teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset &&
                !teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset.is_global
              )
            "
          >
            {{ $t("actions.editFilterPreset") }}</v-button
          >
        </div>
      </div>
    </modal>
    <v2-row>
      <v2-col col12>
        <v2-header-bar :titles="[{ label: $t('titles.projects') }]">
          <template v-slot:title>
            <v2-tooltip
              class="ml-2"
              :title="$t('tips.title')"
              :content="$t('tips.global.search') + '<br/>' + $t('tips.projects.addProject')"
              v-if="showAlways || showTips"
              :show="showTips"
            ></v2-tooltip>
          </template>
          <template v-slot:left>
            <v2-search
              :placeholder="$t('placeholders.searchProject')"
              maxWidth
              @reset="search('projects', getProjects)"
              @submit="search('projects', getProjects)"
              v-model="searchQuery"
            ></v2-search>
          </template>
          <template v-slot:right>
            <v2-button secondary round slot="value" @click.native.prevent="$goTo('QuickLaunch')">
              <v2-icon
                :data="require('@icon/Icon=quick-launch.svg')"
                fill
                :original="true"
                width="20"
                height="auto"
              ></v2-icon>
            </v2-button>
            <v2-button
              class="ml-2"
              :title="$t('labels.newProject')"
              round
              icon
              primary
              @click.native="$goTo('createProject')"
              ><v2-icon :data="require('@icon/Icon=plus.svg')" color="#fff" :original="true"></v2-icon
            ></v2-button>
          </template>
        </v2-header-bar>
      </v2-col>
    </v2-row>
    <v-row>
      <v-col
        class="col-12"
        :class="{
          'pb-0': !teamsSettings[$route.params.team].listsSettings.projects.charts.showCharts,
        }"
      >
        <v-list-header simple>
          <v-list-header-cell
            style="padding-bottom: 0"
            :title="
              teamsSettings[$route.params.team].listsSettings.projects.charts.showCharts ? $t('titles.stats') : ''
            "
          />
          <v2-tooltip
            custom
            placement="top"
            :title="$t('tips.title')"
            :content="$t('tips.projects.showCharts')"
            v-if="showAlways || showTips"
            :show="showTips"
          >
            <template v-slot:element>
              <v-list-header-cell
                style="padding-bottom: 0"
                :simpleLink="
                  teamsSettings[$route.params.team].listsSettings.projects.charts.showCharts
                    ? $t('actions.hideCharts')
                    : $t('actions.showCharts')
                "
                :onClick="
                  () => {
                    toggleShowCharts();
                  }
                "
                :disabledLink="!teamCan('project-statistics')"
                :disabledHandler="
                  () => {
                    $h.showSubscriptionErrorModal(
                      $modal,
                      teams[$route.params.team] && teams[$route.params.team].account.role.includes('owner')
                    );
                  }
                "
              />
            </template>
          </v2-tooltip>
        </v-list-header>
      </v-col>
    </v-row>
    <v-row v-if="loadingCharts" class="relative" style="z-index: 1000"
      ><v-spinner :loading="loadingCharts"></v-spinner
    ></v-row>
    <v-row
      v-if="
        projectsStats && teamsSettings[$route.params.team].listsSettings.projects.charts.showCharts && !loadingCharts
      "
    >
      <v-col class="p-0" v-if="projectsStats.pageviews" @click.native="scrollToAndSetBarChart('pageviews')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.pageviews')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.pageviews.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="pageviewsDoughnutChartData && pageviewsDoughnutChartData.datasets[0].data.length > 0"
            :data="pageviewsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.visitors">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.visitors')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.visitors.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="visitorsDoughnutChartData && visitorsDoughnutChartData.datasets[0].data.length > 0"
            :data="visitorsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.projects" @click.native="scrollToAndSetBarChart('projects')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.projects')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.projects.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="projectsDoughnutChartData && projectsDoughnutChartData.datasets[0].data.length > 0"
            :data="projectsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.users" @click.native="scrollToAndSetBarChart('users-stacked')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.users')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.users.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="usersDoughnutChartData && usersDoughnutChartData.datasets[0].data.length > 0"
            :data="usersDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.contacts" @click.native="scrollToAndSetBarChart('contacts-stacked')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.contacts')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.contacts.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="contactsDoughnutChartData && contactsDoughnutChartData.datasets[0].data.length > 0"
            :data="contactsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.downloads" @click.native="scrollToAndSetBarChart('downloads')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.downloads')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.downloads.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="downloadsDoughnutChartData && downloadsDoughnutChartData.datasets[0].data.length > 0"
            :data="downloadsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.uploads" @click.native="scrollToAndSetBarChart('uploads')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.uploads')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.uploads.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="uploadsDoughnutChartData && uploadsDoughnutChartData.datasets[0].data.length > 0"
            :data="uploadsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.messages" @click.native="scrollToAndSetBarChart('messages')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.messages')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.messages.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="messagesDoughnutChartData && messagesDoughnutChartData.datasets[0].data.length > 0"
            :data="messagesDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col class="p-0" v-if="projectsStats.signings" @click.native="scrollToAndSetBarChart('signings')">
        <v-card class="mx-4 mb-6" height="265" width="203" contentHeight="180" :title="$t('projectsStats.signings')">
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(projectsStats.signings.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="signingsDoughnutChartData && signingsDoughnutChartData.datasets[0].data.length > 0"
            :data="signingsDoughnutChartData"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
      <v-col
        class="p-0"
        v-for="(status, name) in filteredProjectStatusCharts"
        :key="name"
        @click.native="scrollToAndSetBarChart('status', name)"
      >
        <v-card
          class="mx-4 mb-6"
          height="265"
          width="203"
          contentHeight="180"
          :title="$h.formatStatusTitle(projectsStati[name], teams[$route.params.team].account.locale)"
        >
          <div slot="content" class="doughnutChartTotalNumber">
            {{ $h.formatNumber(status.total, 1) }}
          </div>
          <v-doughnut-chart
            v-if="statusDoughnutChartData[name] && statusDoughnutChartData[name].datasets[0].data.length > 0"
            :data="statusDoughnutChartData[name]"
            :options="doughnutChartOptions"
            slot="content"
            class="cardDoughnutChart"
          ></v-doughnut-chart>
        </v-card>
      </v-col>
    </v-row>
    <label
      id="barChart"
      class="ml-4"
      v-if="teamsSettings[$route.params.team].listsSettings.projects.charts.showCharts"
      >{{ currentBarChartTitle ? currentBarChartTitle : "" }}</label
    >
    <v-row v-if="currentBarChartData && teamsSettings[$route.params.team].listsSettings.projects.charts.showCharts">
      <v-col stretch>
        <v-horizontal-bar-chart
          :data="currentBarChartData"
          :options="currentBarChartOptions"
          class="activityChart"
          :onClick="
            (event, points, point) => {
              return filterFromBarChart(event, points, point);
            }
          "
          :style="'height: ' + calcChartHeight(currentBarChartData.datasets[0].data.length) + 'px;'"
        >
        </v-horizontal-bar-chart>
      </v-col>
    </v-row>
    <v-row class="mb-0">
      <v-col stretch>
        <div class="flex flex-wrap">
          <div v-for="preset in filterPresets" :key="preset.name">
            <v-button
              lavender
              small
              @click.native="selectFilterPreset(preset)"
              v-if="preset.is_global"
              class="mr-2.5"
              :selected="
                teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset &&
                preset.uid === teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset.uid
              "
              >{{ $h.formatFilterPresetName(preset, teams[$route.params.team].account.locale) }}</v-button
            >
            <v-combi-button
              v-if="!preset.is_global"
              :selected="
                teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset &&
                preset.uid === teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset.uid
              "
              :first="{
                lavender: true,
                small: true,
                onClick: () => {
                  selectFilterPreset(preset);
                },
              }"
              :second="{
                lavender: true,
                hoverDanger: true,
                small: true,
                icon: 'Icon=x.svg',
                active: true,
                onClick: () => {
                  handleDeleteFilterPreset(preset);
                },
              }"
            >
              {{ $h.formatFilterPresetName(preset, teams[$route.params.team].account.locale) }}</v-combi-button
            >
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row
      v-if="teamsSettings[$route.params.team] && teamsSettings[$route.params.team].listsSettings.projects.filterActive"
    >
      <v-col stretch>
        <v-card custom class="p-6">
          <div slot="content" class="flex flex-row sm:flex-row w-full justify-between">
            <div>
              <div class="flex">
                <div class="text-grey-lavender mb-1">
                  {{
                    !teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset
                      ? $t("filters.results")
                      : $h.formatFilterPresetName(
                          teamsSettings[$route.params.team].listsSettings.projects.currentFilterPreset
                        )
                  }}
                </div>
                <div
                  class="text-danger ml-1"
                  v-if="teamsSettings[$route.params.team].listsSettings.projects.currentFilterPresetHasChanged"
                >
                  &#42;
                </div>
              </div>
              <h6>
                {{
                  $tc(
                    "filters.projectsResult",
                    teamsSettings[$route.params.team].listsSettings.projects.pagination.total,
                    {
                      result: teamsSettings[$route.params.team].listsSettings.projects.pagination.total,
                    }
                  )
                }}
              </h6>
            </div>
            <div>
              <v-button
                class="mt-4 sm:mt-0 mr-2"
                attention
                @click.native="resetFilters(setProjectsFilters, 'projects')"
              >
                {{ $t("filters.resetFilter") }}</v-button
              >
              <v-button
                primary
                :active="teamCan('filter-presets')"
                :withLockIcon="!teamCan('filter-presets')"
                @click.native="handleSaveFilter()"
              >
                {{ $t("actions.saveFilterPreset") }}</v-button
              >
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="projects && teamsSettings[$route.params.team]" style="min-height: 800px">
      <v-col class="col-12">
        <v-list-header complex>
          <v-list-header-cell emptyIcon />
          <v-list-header-cell
            size="3"
            :showAs="$t('project.title')"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="sortMainColDropdown"
          />
          <v-list-header-cell
            :withTooltip="showAlways || showTips"
            :tooltip="{
              content: $t('tips.global.sortList'),
              title: $t('tips.title'),
              showTooltip: showTips,
            }"
            :showAs="$t(teamsSettings[$route.params.team].listsSettings.projects.showAs.firstCol.label)"
            :size="teamsSettings[$route.params.team].listsSettings.projects.showAs.firstCol.size"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="showAsFirstColDropdown"
          />
          <v-list-header-cell
            :showAs="$t(teamsSettings[$route.params.team].listsSettings.projects.showAs.secondCol.label)"
            tabletHidden
            :size="teamsSettings[$route.params.team].listsSettings.projects.showAs.secondCol.size"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="showAsSecondColDropdown"
          />
          <v-list-header-cell
            :showAs="$t(teamsSettings[$route.params.team].listsSettings.projects.showAs.thirdCol.label)"
            dropdownRight
            :size="teamsSettings[$route.params.team].listsSettings.projects.showAs.thirdCol.size"
            :showAsDropdownUid="$h.createUniqueUid()"
            :showAsDropdown="showAsThirdColDropdown"
          />
          <v-list-header-cell
            :mobile="$t('actions.sort')"
            :mobileDropdownUid="$h.createUniqueUid()"
            :mobileDropdown="mobileDropdown"
          />
          <v-list-header-cell emptyIcon />
          <v-list-header-cell
            :withTooltip="showAlways || showTips"
            :tooltip="{
              content: $t('tips.projects.useFilters'),
              title: $t('tips.title'),
              showTooltip: showTips,
            }"
            filter
            dropdownRight
            multiselect
            :columns="3"
            :beforeToggleHandler="loadFilterData"
            :filterActive="teamsSettings[$route.params.team].listsSettings.projects.filterActive"
            :filterDropdownUid="$h.createUniqueUid()"
            :filterDropdown="filterDropdown"
          />
        </v-list-header>
        <ul class="list">
          <router-link
            :to="{
              name: 'Project',
              params: { uid: project.uid, tab: 'overview' },
            }"
            v-for="project in projects"
            :key="project.uid"
          >
            <v-list-item complex hover>
              <v-list-cell icon="Icon=folder-project.svg" iconFill :iconColor="$h.formatStatusColor(project.status)" />
              <v-list-cell
                :title="project.title"
                size="3"
                :mobileSubtitle="
                  $h.formatOwner(project.owner) + (project.owner.company ? ' • ' + project.owner.company : '')
                "
              />
              <v-list-cell
                :size="teamsSettings[$route.params.team].listsSettings.projects.showAs.firstCol.size"
                :value="
                  $h.formatValue(
                    teamsSettings[$route.params.team].listsSettings.projects.showAs.firstCol.format,
                    project[teamsSettings[$route.params.team].listsSettings.projects.showAs.firstCol.key],
                    teams[$route.params.team].account.timezone
                  )
                "
              />
              <v-list-cell
                :size="teamsSettings[$route.params.team].listsSettings.projects.showAs.secondCol.size"
                tabletHidden
                :value="
                  $h.formatValue(
                    teamsSettings[$route.params.team].listsSettings.projects.showAs.secondCol.format,
                    project[teamsSettings[$route.params.team].listsSettings.projects.showAs.secondCol.key],
                    teams[$route.params.team].account.timezone
                  )
                "
              />
              <v-list-cell
                :size="teamsSettings[$route.params.team].listsSettings.projects.showAs.thirdCol.size"
                :value="
                  $h.formatValue(
                    teamsSettings[$route.params.team].listsSettings.projects.showAs.thirdCol.format,
                    project[teamsSettings[$route.params.team].listsSettings.projects.showAs.thirdCol.key],
                    teams[$route.params.team].account.timezone
                  )
                "
              />
              <v-list-cell size="0" showAlways>
                <v2-button
                  secondary
                  round
                  slot="value"
                  @click.native.prevent="
                    $goTo('QuickLaunchWithClientAndProject', {
                      client: project.owner.uid,
                      project: project.uid,
                    })
                  "
                >
                  <v2-icon
                    :data="require('@icon/Icon=quick-launch.svg')"
                    fill
                    :original="true"
                    width="20"
                    height="auto"
                  ></v2-icon>
                </v2-button>
              </v-list-cell>
              <v-list-cell
                iconDropdown
                grey
                icon="Icon=more-horizontal.svg"
                iconColor="#5057af"
                :dropdownData="setMoreDropdownData"
                :dropdownUid="$h.createUniqueUid()"
                :dropdownValue="project"
              />
            </v-list-item>
          </router-link>
          <v-list-item v-if="!projects.length">
            <v-list-cell :value="$t('placeholders.noProjects')"></v-list-cell>
          </v-list-item>
        </ul>
        <v-list-pagination
          :currentPage="teamsSettings[$route.params.team].listsSettings.projects.pagination.page"
          :showPerPage="teamsSettings[$route.params.team].listsSettings.projects.pagination.perPage"
          :totalPages="teamsSettings[$route.params.team].listsSettings.projects.pagination.lastPage"
          :apiCall="
            (page, perPage) => {
              changeProjectsPage(page, perPage);
            }
          "
        >
        </v-list-pagination>
      </v-col>
    </v-row>
  </v2-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import listSettings from "@/mixins/listSettings.js";
import projects from "@/mixins/dropdowns/projects.js";
import filterPresets from "@/mixins/filterPresets.js";
import teamAbilities from "@/mixins/teamAbilities.js";

export default {
  name: "Projects",
  mixins: [listSettings, projects, filterPresets, teamAbilities],
  data() {
    return {
      loadingCharts: false,
      currentDeleteUid: null,
      searchQuery: "",
      filterDataLoaded: false,
      moreDropdown: [
        {
          options: [
            {
              label: this.$t("actions.openBuilder"),
              method: (project) => {
                this.openBuilder(project.uid, false);
              },
              doNotShowActiveState: true,
            },
            {
              label: this.$t("actions.openBuilderPreview"),
              method: (project) => {
                this.openBuilder(project.uid, true);
              },
              doNotShowActiveState: true,
            },
            {
              label: this.$t("actions.share"),
              method: (project) => {
                this.$goTo("Project", { uid: project.uid, tab: "share" });
              },
              doNotShowActiveState: true,
            },
            {
              label: this.$t("actions.setSentStatus"),
              method: (project) => {
                this.setSentStatus(project.uid);
              },
              doNotShowActiveState: true,
              disabled: true,
            },
            {
              label: this.$t("actions.editClient"),
              method: (project) => {
                this.$goTo("editClient", { uid: project.owner.uid });
              },
              doNotShowActiveState: true,
            },
            {
              label: this.$t("actions.duplicateProject"),
              method: (project) => {
                this.showDuplicateModal(project.uid);
              },
              doNotShowActiveState: true,
            },
            {
              label: this.$t("actions.delete"),
              method: (project) => {
                this.showDeleteModal(project.uid);
              },
              color: "text-danger",
              doNotShowActiveState: true,
            },
          ],
        },
      ],
      dateTimeFormat: {
        stringify: (date) => {
          return date
            ? this.$moment(date).tz(this.teams[this.$route.params.team].account.timezone).format("L HH:mm")
            : null;
        },
        parse: (value) => {
          return value
            ? this.$moment(value, "L HH:mm").tz(this.teams[this.$route.params.team].account.timezone).toDate()
            : null;
        },
      },
      clientOptions: [],
      duplicateUid: null,
      duplicateTitle: "",
      duplicateClients: [],
      duplicateFiles: true,
      isLoading: false,
      downloadsDoughnutChartData: null,
      downloadsBarChartData: null,
      uploadsDoughnutChartData: null,
      uploadsBarChartData: null,
      messagesDoughnutChartData: null,
      signingsDoughnutChartData: null,
      messagesBarChartData: null,
      usersDoughnutChartData: null,
      usersBarChartData: null,
      contactsDoughnutChartData: null,
      contactsBarChartData: null,
      pageviewsDoughnutChartData: null,
      pageviewsBarChartData: null,
      projectsDoughnutChartData: null,
      projectsBarChartData: null,
      statusDoughnutChartData: {},
      statusBarChartData: {},
      visitorsDoughnutChartData: null,
      currentBarChartTitle: null,
      currentBarChartData: null,
      currentBarChartOptions: null,
      currentChartIsStacked: false,
      activeFilterBarChartColor: "#000",
      chartsBackgroundColors: [
        "#2F98E8",
        "#154469",
        "#76B9EC",
        "#EAD3B8",
        "#AC998C",
        "#615480",
        "#C2D2BE",
        "#42B8B8",
        "#8E5CFF",
        "#C2C4C8",
        "#462E80",
      ],
      projectsBackgroundColors: ["#FC6D6D", "#FFC64C", "rgb(120, 209, 120)"],
      initBarChartData: {
        labels: [],
        extraLabels: [],
        filterKey: "",
        filterValues: [],
        datasets: [
          {
            data: [],
          },
        ],
      },
      initDoughnutChartData: {
        labels: [],
        extraLabels: [],
        filterKey: "",
        filterValues: [],
        datasets: [
          {
            data: [],
          },
        ],
      },
      barChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                display: false,
              },
              barThickness: 20,
              ticks: {
                fontFamily: "Cabin",
                fontStyle: "bold",
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                color: "#eee",
              },
              ticks: {
                beginAtZero: true,
                userCallback: (label) => {
                  if (Math.floor(label) === label) {
                    return label;
                  }
                },
              },
            },
          ],
        },
        tooltips: {
          position: "custom",
          backgroundColor: "#FFFFFF",
          bodyFontColor: "#000000",
          borderWidth: 1,
          borderColor: "#eee",
          bodyFontFamily: "Cabin",
          displayColors: false,
          callbacks: {
            label: (tooltipItem, data) => {
              let label = data.extraLabels[tooltipItem.index] || "";
              let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " " + label;
            },
            title: () => {
              return "";
            },
          },
        },
        plugins: {
          datalabels: {
            font: {
              family: "Cabin",
            },
            align: -45,
            anchor: "start",
            offset: 14,
            formatter: (value, context) => {
              return context.chart.data.extraLabels[context.dataIndex];
            },
          },
        },
      },
      doughnutChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        tooltips: {
          //   position: 'custom',
          //   backgroundColor: '#FFFFFF',
          //   bodyFontColor: '#000000',
          //   borderWidth: 1,
          //   borderColor: '#eee',
          bodyFontFamily: "Cabin",
          displayColors: false,
          callbacks: {
            label: (tooltipItem, data) => {
              let label = data.extraLabels[tooltipItem.index] || "";
              let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " " + label;
            },
            title: () => {
              return "";
            },
          },
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
      },
      stackedBarChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
              },
              barThickness: 20,
              ticks: {
                fontFamily: "Cabin",
                fontStyle: "bold",
              },
            },
          ],
          xAxes: [
            {
              stacked: true,
              gridLines: {
                color: "#eee",
              },
              ticks: {
                beginAtZero: true,
                userCallback: (label) => {
                  if (Math.floor(label) === label) {
                    return label;
                  }
                },
              },
            },
          ],
        },
        tooltips: {
          mode: "single",
          position: "custom",
          backgroundColor: "#FFFFFF",
          bodyFontColor: "#000000",
          borderWidth: 1,
          borderColor: "#eee",
          bodyFontFamily: "Cabin",
          displayColors: false,
          callbacks: {
            label: (tooltipItem, data) => {
              let label = data.datasets[tooltipItem.datasetIndex].label || "";
              let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return value + " " + label;
            },
            title: () => {
              return "";
            },
          },
        },
        plugins: {
          datalabels: {
            font: {
              family: "Cabin",
            },
            align: -45,
            anchor: "start",
            offset: 14,
            formatter: (value, context) => {
              return context.datasetIndex === 0 ? context.chart.data.extraLabels[context.dataIndex] : "";
            },
          },
        },
      },
    };
  },
  created() {
    if (this.teams[this.$route.params.team].account) {
      this.setDateTimeFormat();
      this.loadPageData();
    }
    if (
      this.teamsSettings[this.$route.params.team] &&
      this.teamsSettings[this.$route.params.team].listsSettings.projects.search
    )
      this.searchQuery = this.teamsSettings[this.$route.params.team].listsSettings.projects.search;
  },
  methods: {
    ...mapActions([
      "setListsSettings",
      "getProjectList",
      "getTeamTemplates",
      "getTeamContacts",
      "deleteProject",
      "setMenus",
      "addCustomFilterToListsettings",
      "setProjectsCustomFields",
      "getProjectBuilderSession",
      "getClientList",
      "duplicateProject",
      "toggleShowProjectsStats",
      "getFilterPresets",
      "getFilterPreset",
      "createFilterPreset",
      "editFilterPreset",
      "deleteFilterPreset",
      "setCurrentFilterPreset",
      "setCurrentFilterPresetHasChanged",
      "getUsers",
      "getTeamTags",
      "getProjectsStats",
      "setProjectPosted",
    ]),
    handleSaveFilter() {
      if (!this.teamCan("filter-presets")) {
        this.$h.showSubscriptionErrorModal(
          this.$modal,
          this.teams[this.$route.params.team] && this.teams[this.$route.params.team].account.role.includes("owner")
        );
      } else {
        this.showSaveFilterPresetModal("projects");
      }
    },
    handleDeleteFilterPreset(preset) {
      if (!this.teamCan("filter-presets")) {
        this.$h.showSubscriptionErrorModal(
          this.$modal,
          this.teams[this.$route.params.team] && this.teams[this.$route.params.team].account.role.includes("owner")
        );
      } else {
        this.showDeleteFilterPresetModal(preset);
      }
    },
    openBuilder(uid, preview = false) {
      this.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      this.getProjectBuilderSession({
        params: {
          project: uid,
        },
        data: { return_url: window.location.href, preview: preview },
      })
        .then(() => {
          location.href = this.builderSession;
        })
        .catch(() => {
          this.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    setDateTimeFormat() {
      for (let i = 0; i < this.filterDropdown[0].options.length; i += 1) {
        if (this.filterDropdown[0].options[i].dateTimeRange) {
          this.filterDropdown[0].options[i].dateTimeRange.dateTimeFormat = this.dateTimeFormat;
          this.filterDropdown[0].options[i].dateTimeRange.lang = this.teams[this.$route.params.team].account.locale;
        }
      }
    },
    setCustomFields(customFields) {
      var init = false;
      if (!this.projectsCustomFields) init = true;
      this.setProjectsCustomFields(customFields).then(() => {
        if (init) this.setCustomFilters();
      });
    },
    setCustomFilters() {
      if (this.projectsCustomFields) {
        Object.keys(this.projectsCustomFields).forEach((key) => {
          if (this.projectsCustomFields[key].filterable) {
            let newWithOption = {
              label: this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                ? this.$t("filters.with") +
                  " " +
                  this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                : this.$t("filters.with") + " " + this.projectsCustomFields[key].title.default,
              column: 2,
              select: {
                custom: true,
                name: "with_custom_" + key,
                multiple: true,
                model: [],
                options: [],
                label: "label",
                placeholder: this.$t("placeholders.selectCustom"),
                wrap: true,
                searchable: true,
                searchChange: () => {},
              },
              method: () => {},
              checkbox: true,
              hoverBg: true,
              hideIfNoOptions: true,
            };
            this.$set(newWithOption, "active", false);
            let newWithoutOption = {
              label: this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                ? this.$t("filters.without") +
                  " " +
                  this.projectsCustomFields[key].title[this.teams[this.$route.params.team].account.locale]
                : this.$t("filters.without") + " " + this.projectsCustomFields[key].title.default,
              column: 3,
              select: {
                custom: true,
                name: "without_custom_" + key,
                multiple: true,
                model: [],
                options: [],
                label: "label",
                placeholder: this.$t("placeholders.selectCustom"),
                wrap: true,
                searchable: true,
                searchChange: () => {},
              },
              method: () => {},
              checkbox: true,
              hoverBg: true,
              hideIfNoOptions: true,
            };
            this.$set(newWithoutOption, "active", false);
            this.addCustomFilterToListsettings({
              list: "projects",
              customFilter: "with_custom_" + key,
            });
            this.addCustomFilterToListsettings({
              list: "projects",
              customFilter: "without_custom_" + key,
            });
            const index = this.filterDropdown[0].options.length - 3;
            this.filterDropdown[0].options.splice(index, 0, newWithoutOption);
            this.filterDropdown[0].options.splice(index, 0, newWithOption);
          }
        });
      }
    },
    loadPageData() {
      const vm = this;
      vm.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      this.setCustomFilters();
      this.loadFilterPresets("projects");
      let p = {
        search: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.search,
        page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.page,
        per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.perPage,
        order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.col,
        order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.dir,
        with_pageviewed: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.filter.with_pageviewed.model,
      };
      let params = vm.setFilterRequestObjectFromStore("projects", p);
      if (vm.teamsSettings[vm.$route.params.team].listsSettings.projects.charts.showCharts) {
        this.loadingCharts = true;
        vm.getProjectsStats({
          setListsSettings: false,
          customLoading: true,
          timezone: this.teams[this.$route.params.team].account.timezone,
          params,
        })
          .then(() => {
            this.loadingCharts = false;
            if (vm.projectsStats) vm.setChartsData();
            if (vm.$route.query.barChart) {
              vm.scrollToAndSetBarChart(vm.$route.query.barChart);
            }
          })
          .catch(() => {
            this.loadingCharts = false;
          });
      }
      vm.getProjectList({
        setListsSettings: true,
        timezone: this.teams[this.$route.params.team].account.timezone,
        params,
      })
        .then((res) => {
          vm.setCustomFields(res.data.meta.custom);
          vm.initSort("projects");
          vm.initShowAs("projects");
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        })
        .catch((err) => {
          console.log(">>>>>>>>>>>>>>> ERROR", err);
          vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
        });
    },
    setMoreDropdownData(project) {
      return new Promise((resolve) => {
        if (
          (project.status & this.projectsStati.POSTED.bit) !== this.projectsStati.POSTED.bit &&
          (project.status & this.projectsStati.VISITED.bit) !== this.projectsStati.VISITED.bit
        ) {
          this.moreDropdown[0].options[3].disabled = false;
        } else {
          this.moreDropdown[0].options[3].disabled = true;
        }
        resolve(this.moreDropdown);
      });
    },
    loadFilterData() {
      return new Promise((resolve, reject) => {
        if (!this.filterDataLoaded) {
          const vm = this;
          const p1 = vm.getTeamTemplates({ params: { per_page: "1000" }, customLoading: true });
          const params = {
            per_page: "1000",
            order_col: "last_name",
            order_dir: "ASC",
            search: "",
          };
          const p2 = vm.getTeamContacts({ params, customLoading: true });
          const p3 = vm.getUsers({ params, customLoading: true });
          const p4 = vm.getTeamTags({ params, customLoading: true });
          Promise.all([p1, p2, p3, p4])
            .then(() => {
              vm.initFilters("projects");
              vm.setDropdownOptions();
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              resolve();
              this.filterDataLoaded = true;
            })
            .catch((err) => {
              console.log(">>>>>>>>>>>>>>> ERROR", err);
              vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              reject();
            });
        } else {
          resolve();
        }
      });
    },
    sortProjects(col, dir) {
      const params = {
        search: this.searchQuery,
        page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.page,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.perPage,
        order_col: col,
        order_dir: dir,
      };
      this.getProjects(params);
    },
    changeProjectsPage(page, perPage) {
      console.log("search", this.searchQuery, this.searchQuery);
      const params = {
        search: this.searchQuery,
        page,
        per_page: perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.dir,
      };
      this.getProjects(params);
    },
    setProjectsFilters() {
      // remove url parameter which possible affects the filter
      // if the filter was newly set
      const urlParams = new URLSearchParams(window.location.search);
      const queryParamFrom = urlParams.get("from");
      if (queryParamFrom) {
        let pathname = location.pathname;
        window.history.pushState("", "", pathname);
      }

      const params = {
        search: this.searchQuery,
        page: 1,
        per_page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.perPage,
        order_col: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.col,
        order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.dir,
      };
      this.getProjects(params, true);
      this.$eventbus.$emit("closeAllDropdowns");
    },
    getProjects(params, withStats = false) {
      const vm = this;
      this.filterDropdown[0].options.forEach((option) => {
        if (option.active && option.switch && option.switch.name && !option.select) {
          params[option.switch.name] = option.switch.model;
        }
        if (option.active && option.select && !option.select.custom) {
          params[option.select.name] = this.transformModelToRequestArray(option.select.model);
        }
        if (option.active && option.select && option.select.custom) {
          const filterName = option.select.name.split("_").slice(0, 2).join("_");
          const customFieldName = option.select.name.split("_").slice(2).join("_");
          if (!params[filterName]) params[filterName] = [];
          this.transformModelToRequestArray(option.select.model, customFieldName).forEach((model) => {
            params[filterName].push(model);
          });
        }
        if (option.dateTimeRange) {
          if (
            option.dateTimeRange.model.length === 2 &&
            option.dateTimeRange.model[0] !== "" &&
            option.dateTimeRange.model[1] !== ""
          ) {
            params["time_from"] = this.$moment(option.dateTimeRange.model[0])
              .tz(this.teams[this.$route.params.team].account.timezone)
              .format();
            params["time_to"] = this.$moment(option.dateTimeRange.model[1])
              .tz(this.teams[this.$route.params.team].account.timezone)
              .format();
          } else {
            params["time_from"] = "";
            params["time_to"] = "";
          }
        }
      });
      if (params.time_relative && params.time_relative !== "custom" && params.time_from && params.time_to) {
        params.time_from = "";
        params.time_to = "";
      }
      this.getProjectList({
        setListsSettings: true,
        params,
      }).then(() => {
        this.checkCurrentFilterPresetHasChanged("projects");
        this.initSort("projects");
      });
      if (vm.teamsSettings[vm.$route.params.team].listsSettings.projects.charts.showCharts && withStats) {
        this.loadStats(params);
      }
    },
    getStats(params) {
      this.filterDropdown[0].options.forEach((option) => {
        if (option.active && option.switch && option.switch.name && !option.select) {
          params[option.switch.name] = option.switch.model;
        }
        if (option.active && option.select && !option.select.custom) {
          params[option.select.name] = this.transformModelToRequestArray(option.select.model);
        }
        if (option.active && option.select && option.select.custom) {
          const filterName = option.select.name.split("_").slice(0, 2).join("_");
          const customFieldName = option.select.name.split("_").slice(2).join("_");
          if (!params[filterName]) params[filterName] = [];
          this.transformModelToRequestArray(option.select.model, customFieldName).forEach((model) => {
            params[filterName].push(model);
          });
        }
        if (option.dateTimeRange) {
          if (
            option.dateTimeRange.model.length === 2 &&
            option.dateTimeRange.model[0] !== "" &&
            option.dateTimeRange.model[1] !== ""
          ) {
            params["time_from"] = this.$moment(option.dateTimeRange.model[0])
              .tz(this.teams[this.$route.params.team].account.timezone)
              .format();
            params["time_to"] = this.$moment(option.dateTimeRange.model[1])
              .tz(this.teams[this.$route.params.team].account.timezone)
              .format();
          } else {
            params["time_from"] = "";
            params["time_to"] = "";
          }
        }
      });
      if (params.time_relative && params.time_relative !== "custom" && params.time_from && params.time_to) {
        params.time_from = "";
        params.time_to = "";
      }
      this.loadStats(params);
    },
    loadStats(params) {
      const vm = this;
      if (vm.teamsSettings[vm.$route.params.team].listsSettings.projects.charts.showCharts) {
        this.loadingCharts = true;
        vm.getProjectsStats({
          setListsSettings: false,
          customLoading: true,
          timezone: vm.teams[vm.$route.params.team].account.timezone,
          params,
        })
          .then(() => {
            if (vm.projectsStats) vm.setChartsData();
            vm.loadingCharts = false;
          })
          .catch(() => {
            vm.loadingCharts = false;
          });
      }
    },
    initChartsData() {
      this.downloadsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.downloadsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.uploadsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.uploadsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.messagesBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.messagesDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.signingsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.usersBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.usersDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.contactsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.contactsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.pageviewsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.pageviewsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.projectsBarChartData = JSON.parse(JSON.stringify(this.initBarChartData));
      this.projectsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      this.visitorsDoughnutChartData = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      Object.keys(this.projectsStats.status).forEach((key) => {
        this.statusBarChartData[key] = JSON.parse(JSON.stringify(this.initBarChartData));
        this.statusDoughnutChartData[key] = JSON.parse(JSON.stringify(this.initDoughnutChartData));
      });
    },
    setChartsData() {
      this.initChartsData();
      this.setChartData(this.downloadsBarChartData, this.projectsStats.downloads, "downloads");
      this.setChartData(this.downloadsDoughnutChartData, this.projectsStats.downloads, "downloads");
      this.setChartData(this.uploadsBarChartData, this.projectsStats.uploads, "uploads");
      this.setChartData(this.uploadsDoughnutChartData, this.projectsStats.uploads, "uploads");
      this.setChartData(this.messagesBarChartData, this.projectsStats.messages, "messages");
      this.setChartData(this.messagesDoughnutChartData, this.projectsStats.messages, "messages");
      this.setChartData(this.signingsDoughnutChartData, this.projectsStats.signings, "signings");
      this.setChartData(this.usersBarChartData, this.projectsStats.users, "users-stacked");
      this.setChartData(this.usersDoughnutChartData, this.projectsStats.users, "users");
      this.setChartData(this.contactsBarChartData, this.projectsStats.contacts, "contacts-stacked");
      this.setChartData(this.contactsDoughnutChartData, this.projectsStats.contacts, "contacts");
      this.setChartData(this.pageviewsBarChartData, this.projectsStats.pageviews, "pageviews");
      this.setChartData(this.pageviewsDoughnutChartData, this.projectsStats.pageviews, "pageviews");
      this.setChartData(this.projectsBarChartData, this.projectsStats.projects, "projects");
      this.setChartData(this.projectsDoughnutChartData, this.projectsStats.projects, "projects");
      this.setChartData(this.visitorsDoughnutChartData, this.projectsStats.visitors, "visitors");
      Object.keys(this.projectsStats.status).forEach((key) => {
        this.setChartData(this.statusDoughnutChartData[key], this.projectsStats.status[key], "status", key);
        this.setChartData(this.statusBarChartData[key], this.projectsStats.status[key], "status", key);
      });
      this.resetBarChart();
    },
    setChartData(chart, stats, chartName, key = "") {
      let label = "";
      let arr = [];
      let data = [];
      let datasets = [];
      let d = {
        new: [],
        visited: [],
        posted: [],
      };
      chart.extraLabels = [];
      chart.labels = [];
      switch (chartName) {
        case "downloads":
          label = "Downloads";
          chart.extraLabels.push(this.$t("projectsStats.with" + label));
          chart.labels.push(stats.items.projects.total);
          data.push(stats.items.projects.total);
          chart.extraLabels.push(this.$t("projectsStats.without" + label));
          chart.labels.push(stats.items.na.total);
          data.push(stats.items.na.total);
          chart.filterKey = "has_downloads";
          chart.filterValues = [true, false];
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("downloads", chart.filterKey, data[0] === 0),
            align: "start",
          });
          break;
        case "uploads":
          label = "Uploads";
          chart.extraLabels.push(this.$t("projectsStats.with" + label));
          chart.labels.push(stats.items.projects.total);
          data.push(stats.items.projects.total);
          chart.extraLabels.push(this.$t("projectsStats.without" + label));
          chart.labels.push(stats.items.na.total);
          data.push(stats.items.na.total);
          chart.filterKey = "has_uploads";
          chart.filterValues = [true, false];
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("uploads", chart.filterKey, data[0] === 0),
            align: "start",
          });
          break;
        case "messages":
          label = "Messages";
          chart.extraLabels.push(this.$t("projectsStats.with" + label));
          chart.labels.push(stats.items.projects.total);
          data.push(stats.items.projects.total);
          chart.extraLabels.push(this.$t("projectsStats.without" + label));
          chart.labels.push(stats.items.na.total);
          data.push(stats.items.na.total);
          chart.filterKey = "has_messages";
          chart.filterValues = [true, false];
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("messages", chart.filterKey, data[0] === 0),
            align: "start",
          });
          break;
        case "pageviews":
          Object.keys(stats.items).forEach((key) => {
            chart.extraLabels.push(stats.items[key].template_name + " - " + stats.items[key].page_name);
            chart.labels.push(stats.items[key].total);
            data.push(stats.items[key].total);
            chart.filterKey = "with_pageviewed";
            chart.filterValues.push(stats.items[key].page_uid);
          });
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor(
              "pageviews",
              chart.filterKey,
              data[0] === 0,
              chart.filterValues
            ),
            align: "start",
          });
          break;
        case "projects":
          label = "Projects";
          chart.extraLabels.push(this.$t("projectsStats.new" + label));
          chart.labels.push(stats.items.new.total);
          data.push(stats.items.new.total);
          chart.extraLabels.push(this.$t("projectsStats.posted" + label));
          chart.labels.push(stats.items.posted.total);
          data.push(stats.items.posted.total);
          chart.extraLabels.push(this.$t("projectsStats.visited" + label));
          chart.labels.push(stats.items.visited.total);
          data.push(stats.items.visited.total);
          chart.filterKey = ["is_posted", "is_visited"];
          chart.filterValues = [
            [false, false],
            [true, false],
            ["", true],
          ];
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("projects", chart.filterKey, false),
            align: "start",
          });
          break;
        case "signings":
          label = "Signings";
          chart.extraLabels.push(this.$t("projectsStats.with" + label));
          chart.labels.push(stats.items.projects.total);
          data.push(stats.items.projects.total);
          chart.extraLabels.push(this.$t("projectsStats.without" + label));
          chart.labels.push(stats.items.na.total);
          data.push(stats.items.na.total);
          chart.filterKey = "has_downloads";
          chart.filterValues = [true, false];
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("signings", chart.filterKey, data[0] === 0),
            align: "start",
          });
          break;
        case "status":
          chart.extraLabels.push(
            this.$t("projectsStats.withStatus") +
              " " +
              this.$h.formatStatusTitle(this.projectsStati[key], this.teams[this.$route.params.team].account.locale)
          );
          chart.labels.push(stats.items.projects.total);
          data.push(stats.items.projects.total);
          chart.extraLabels.push(
            this.$t("projectsStats.withoutStatus") +
              " " +
              this.$h.formatStatusTitle(this.projectsStati[key], this.teams[this.$route.params.team].account.locale)
          );
          chart.labels.push(stats.items.na.total);
          data.push(stats.items.na.total);
          chart.filterKey = ["with_status", "without_status"];
          chart.filterValues = [
            [key, ""],
            ["", key],
          ];
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("status", chart.filterKey, data[0] === 0, key),
            align: "start",
          });
          break;
        case "visitors":
          chart.extraLabels.push(this.$t("projectsStats.visitors"));
          chart.labels.push(stats.total);
          data.push(stats.total);
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.chartsBackgroundColors,
            align: "start",
          });
          break;
        case "users":
          Object.keys(stats.items).forEach((key) => {
            chart.extraLabels.push(stats.items[key].user_first_name + " " + stats.items[key].user_last_name);
            chart.labels.push("4");
            data.push(stats.items[key].total);
          });
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("users", ["is_posted", "is_visited"], false),
            align: "start",
          });
          break;
        case "users-stacked":
          chart.filterKey = ["is_posted", "is_visited", "with_user"];
          chart.filterValues = [
            [false, false],
            [true, false],
            ["", true],
          ];
          Object.keys(stats.items).forEach((key) => {
            chart.labels.push(stats.items[key].total);
            chart.extraLabels.push(stats.items[key].user_first_name + " " + stats.items[key].user_last_name);
            d.new.push(stats.items[key].new);
            d.visited.push(stats.items[key].visited);
            d.posted.push(stats.items[key].posted);
            arr.push(stats.items[key].user_uid);
          });
          chart.filterValues.forEach((value) => {
            value.push(arr);
          });
          datasets.push({
            label: this.$t("stati.listValues.new"),
            data: d.new,
            backgroundColor: "rgb(255, 60, 81)",
          });
          datasets.push({
            label: this.$t("stati.listValues.sent"),
            data: d.posted,
            backgroundColor: "#FFC64C",
          });
          datasets.push({
            label: this.$t("stati.listValues.visited"),
            data: d.visited,
            backgroundColor: "rgb(120, 209, 120)",
          });
          chart.datasets = datasets;
          break;
        case "contacts":
          Object.keys(stats.items).forEach((key) => {
            chart.extraLabels.push(stats.items[key].contact_first_name + " " + stats.items[key].contact_last_name);
            chart.labels.push("4");
            data.push(stats.items[key].total);
          });
          this.$set(chart.datasets, 0, {
            data: data,
            backgroundColor: this.setActiveFilterBarChartColor("contacts", ["is_posted", "is_visited"], false),
            align: "start",
          });
          break;
        case "contacts-stacked":
          chart.filterKey = ["is_posted", "is_visited", "with_contact"];
          chart.filterValues = [
            [false, false],
            [true, false],
            ["", true],
          ];
          Object.keys(stats.items).forEach((key) => {
            chart.labels.push(stats.items[key].total);
            chart.extraLabels.push(stats.items[key].contact_first_name + " " + stats.items[key].contact_last_name);
            d.new.push(stats.items[key].new);
            d.visited.push(stats.items[key].visited);
            d.posted.push(stats.items[key].posted);
            arr.push(stats.items[key].contact_uid);
          });
          chart.filterValues.forEach((value) => {
            value.push(arr);
          });
          datasets.push({
            label: this.$t("stati.listValues.new"),
            data: d.new,
            backgroundColor: "rgb(255, 60, 81)",
          });
          datasets.push({
            label: this.$t("stati.listValues.sent"),
            data: d.posted,
            backgroundColor: "#FFC64C",
          });
          datasets.push({
            label: this.$t("stati.listValues.visited"),
            data: d.visited,
            backgroundColor: "rgb(120, 209, 120)",
          });
          chart.datasets = datasets;
          break;
        default:
      }
    },
    setActiveFilterBarChartColor(key, filterKey, changeFirstColor, value = null) {
      let colors = JSON.parse(JSON.stringify(this.chartsBackgroundColors));
      if (changeFirstColor) colors.unshift("white");
      if (key === "projects") {
        colors = JSON.parse(JSON.stringify(this.projectsBackgroundColors));
        let isPosted = null;
        let isVisited = null;
        this.filterDropdown[0].options.forEach((option) => {
          if (option.switch && option.switch.name === filterKey[0]) {
            if (option.active) isPosted = option.switch.model;
          }
          if (option.switch && option.switch.name === filterKey[1]) {
            if (option.active) isVisited = option.switch.model;
          }
        });
        if (!isPosted && !isVisited && isPosted !== null && isVisited !== null) {
          colors[0] = this.activeFilterBarChartColor;
        } else if (isPosted && !isVisited && isVisited !== null) {
          colors[1] = this.activeFilterBarChartColor;
        } else if (isPosted == null && isVisited) {
          colors[2] = this.activeFilterBarChartColor;
        }
      } else if (key === "downloads" || key === "uploads" || key === "messages" || key === "signings") {
        this.filterDropdown[0].options.forEach((option) => {
          if (option.switch && option.switch.name === filterKey) {
            if (option.active) {
              if (option.switch.model) {
                colors[0] = this.activeFilterBarChartColor;
              } else {
                colors[1] = this.activeFilterBarChartColor;
              }
            }
          }
        });
      } else if (key === "status") {
        this.filterDropdown[0].options.forEach((option) => {
          if (option.active && option.select && option.select.name === filterKey[0]) {
            option.select.model.forEach((obj) => {
              if (obj.value === value) colors[0] = this.activeFilterBarChartColor;
            });
          }
          if (option.active && option.select && option.select.name === filterKey[1]) {
            option.select.model.forEach((obj) => {
              if (obj.value === value) colors[1] = this.activeFilterBarChartColor;
            });
          }
        });
      } else if (key === "pageviews") {
        value.forEach((val, key) => {
          this.teamsSettings[this.$route.params.team].listsSettings.projects.filter.with_pageviewed.model.forEach(
            (v) => {
              if (val === v) {
                colors[key] = this.activeFilterBarChartColor;
              }
            }
          );
        });
      } else if (key === "users") {
        let isWithUser = false;
        this.filterDropdown[0].options.forEach((option) => {
          if (option.select && option.select.name === "with_user" && option.active) {
            isWithUser = true;
          }
        });
        if (isWithUser) {
          let isPosted = null;
          let isVisited = null;
          this.filterDropdown[0].options.forEach((option) => {
            if (option.switch && option.switch.name === filterKey[0]) {
              if (option.active) isPosted = option.switch.model;
            }
            if (option.switch && option.switch.name === filterKey[1]) {
              if (option.active) isVisited = option.switch.model;
            }
          });
          if (!isPosted && !isVisited && isPosted !== null && isVisited !== null) {
            colors[0] = this.activeFilterBarChartColor;
          } else if (isPosted && !isVisited && isVisited !== null) {
            colors[0] = this.activeFilterBarChartColor;
          } else if (isPosted == null && isVisited) {
            colors[0] = this.activeFilterBarChartColor;
          }
        }
      } else if (key === "contacts") {
        let isWithContact = false;
        this.filterDropdown[0].options.forEach((option) => {
          if (option.select && option.select.name === "with_contact" && option.active) {
            isWithContact = true;
          }
        });
        if (isWithContact) {
          let isPosted = null;
          let isVisited = null;
          this.filterDropdown[0].options.forEach((option) => {
            if (option.switch && option.switch.name === filterKey[0]) {
              if (option.active) isPosted = option.switch.model;
            }
            if (option.switch && option.switch.name === filterKey[1]) {
              if (option.active) isVisited = option.switch.model;
            }
          });
          if (!isPosted && !isVisited && isPosted !== null && isVisited !== null) {
            colors[0] = this.activeFilterBarChartColor;
          } else if (isPosted && !isVisited && isVisited !== null) {
            colors[0] = this.activeFilterBarChartColor;
          } else if (isPosted == null && isVisited) {
            colors[0] = this.activeFilterBarChartColor;
          }
        }
      }
      return colors;
    },
    calcChartHeight(count) {
      let height = 15;
      let factor = 70;
      for (let i = 0; i < count; i += 1) {
        height += factor;
        factor -= factor * 0.01;
      }
      return height;
    },
    toggleShowCharts() {
      const data = {
        list: "projects",
        charts: {
          showCharts: !this.teamsSettings[this.$route.params.team].listsSettings.projects.charts.showCharts,
        },
      };
      this.$store.commit("GLOBAL_SET_LISTS_SETTINGS", data);
      if (this.teamsSettings[this.$route.params.team].listsSettings.projects.charts.showCharts) {
        const params = {
          search: this.searchQuery,
          page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.page,
          per_page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.perPage,
          order_col: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.col,
          order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.dir,
        };
        this.getStats(params);
      }
    },
    scrollToAndSetBarChart(chart, name = null) {
      console.log("scrollToAndSetBarChart");
      const data = {
        list: "projects",
        charts: {
          currentBarChart: chart,
          currentBarChartName: name,
        },
      };
      this.$store.commit("GLOBAL_SET_LISTS_SETTINGS", data);
      switch (chart) {
        case "pageviews":
          this.currentBarChartTitle = this.$t("projectsStats.pageviews");
          this.currentBarChartData = this.pageviewsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "projects":
          this.currentBarChartTitle = this.$t("projectsStats.projects");
          this.currentBarChartData = this.projectsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "downloads":
          this.currentBarChartTitle = this.$t("projectsStats.downloads");
          this.currentBarChartData = this.downloadsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "uploads":
          this.currentBarChartTitle = this.$t("projectsStats.uploads");
          this.currentBarChartData = this.uploadsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "messages":
          this.currentBarChartTitle = this.$t("projectsStats.messages");
          this.currentBarChartData = this.messagesBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "users":
          this.currentBarChartTitle = this.$t("projectsStats.users");
          this.currentBarChartData = this.usersBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "users-stacked":
          this.currentBarChartTitle = this.$t("projectsStats.users");
          this.currentBarChartData = this.usersBarChartData;
          this.currentBarChartOptions = this.stackedBarChartOptions;
          this.currentChartIsStacked = true;
          break;
        case "contacts":
          this.currentBarChartTitle = this.$t("projectsStats.contacts");
          this.currentBarChartData = this.contactsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "contacts-stacked":
          this.currentBarChartTitle = this.$t("projectsStats.contacts");
          this.currentBarChartData = this.contactsBarChartData;
          this.currentBarChartOptions = this.stackedBarChartOptions;
          this.currentChartIsStacked = true;
          break;
        case "status":
          this.currentBarChartTitle = this.$h.formatStatusTitle(
            this.projectsStati[name],
            this.teams[this.$route.params.team].account.locale
          );
          this.currentBarChartData = this.statusBarChartData[name];
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
      }
      this.$scrollTo("#barChart", 500, { offset: -80 });
    },
    resetBarChart() {
      switch (this.teamsSettings[this.$route.params.team].listsSettings.projects.charts.currentBarChart) {
        case "pageviews":
          this.currentBarChartTitle = this.$t("projectsStats.pageviews");
          this.currentBarChartData = this.pageviewsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "projects":
          this.currentBarChartTitle = this.$t("projectsStats.projects");
          this.currentBarChartData = this.projectsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "downloads":
          this.currentBarChartTitle = this.$t("projectsStats.downloads");
          this.currentBarChartData = this.downloadsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "uploads":
          this.currentBarChartTitle = this.$t("projectsStats.uploads");
          this.currentBarChartData = this.uploadsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "messages":
          this.currentBarChartTitle = this.$t("projectsStats.messages");
          this.currentBarChartData = this.messagesBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "users":
          this.currentBarChartTitle = this.$t("projectsStats.users");
          this.currentBarChartData = this.usersBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "users-stacked":
          this.currentBarChartTitle = this.$t("projectsStats.users");
          this.currentBarChartData = this.usersBarChartData;
          this.currentBarChartOptions = this.stackedBarChartOptions;
          this.currentChartIsStacked = true;
          break;
        case "contacts":
          this.currentBarChartTitle = this.$t("projectsStats.contacts");
          this.currentBarChartData = this.contactsBarChartData;
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
        case "contacts-stacked":
          this.currentBarChartTitle = this.$t("projectsStats.contacts");
          this.currentBarChartData = this.contactsBarChartData;
          this.currentBarChartOptions = this.stackedBarChartOptions;
          this.currentChartIsStacked = true;
          break;
        case "status":
          this.currentBarChartTitle = this.$h.formatStatusTitle(
            this.projectsStati[
              this.teamsSettings[this.$route.params.team].listsSettings.projects.charts.currentBarChartName
            ],
            this.teams[this.$route.params.team].account.locale
          );
          if (this.teamsSettings[this.$route.params.team].listsSettings.projects.charts.currentBarChartName) {
            this.currentBarChartData =
              this.statusBarChartData[
                this.teamsSettings[this.$route.params.team].listsSettings.projects.charts.currentBarChartName
              ];
          }
          this.currentBarChartOptions = this.barChartOptions;
          this.currentChartIsStacked = false;
          break;
      }
    },
    filterFromBarChart(event, points, stackedPoint) {
      if (points && points[0]) {
        let index = points[0]._index;
        if (this.currentChartIsStacked) index = stackedPoint._datasetIndex;
        // special case cause filter is not in filter dropdown
        let filterPages = [];
        if (this.currentBarChartData.filterKey === "with_pageviewed") {
          filterPages = JSON.parse(
            JSON.stringify(
              this.teamsSettings[this.$route.params.team].listsSettings.projects.filter.with_pageviewed.model
            )
          );
          if (filterPages.length < 1) {
            filterPages.push(this.currentBarChartData.filterValues[index]);
          } else {
            let spliceIndex = -1;
            for (let i = 0; i < filterPages.length; i += 1) {
              if (this.currentBarChartData.filterValues[index] === filterPages[i]) {
                spliceIndex = i;
              }
            }
            if (spliceIndex >= 0) {
              filterPages.splice(spliceIndex, 1);
            } else {
              filterPages.push(this.currentBarChartData.filterValues[index]);
            }
          }
        } else {
          // else handle normal filter
          this.filterDropdown[0].options.forEach((option) => {
            if (Array.isArray(this.currentBarChartData.filterKey)) {
              this.currentBarChartData.filterKey.forEach((value, key) => {
                if (option.select && option.select.name === value) {
                  if (!option.active) {
                    this.currentBarChartData.filterValues[index].forEach((val) => {
                      for (let i = 0; i < option.select.model.length; i += 1) {
                        if (option.select.model[i].value === val) {
                          option.select.model.splice(i, 1);
                          if (option.select.model.length < 1) {
                            option.active = false;
                          }
                        }
                      }
                    });
                    // set value
                    let title;
                    if (
                      this.currentBarChartData.filterKey[key] === "with_status" ||
                      this.currentBarChartData.filterKey[key] === "without_status"
                    ) {
                      if (this.currentBarChartData.filterValues[index][key] !== "") {
                        title = this.projectsStati[this.currentBarChartData.filterValues[index][key]].title[
                          this.teams[this.$route.params.team].account.locale
                        ]
                          ? this.projectsStati[this.currentBarChartData.filterValues[index][key]].title[
                              this.teams[this.$route.params.team].account.locale
                            ]
                          : this.projectsStati[this.currentBarChartData.filterValues[index][key]].title.default;
                      }
                      value = this.currentBarChartData.filterValues[index][key];
                    } else if (
                      this.currentBarChartData.filterKey[key] === "with_user" ||
                      this.currentBarChartData.filterKey[key] === "without_user"
                    ) {
                      title = this.$h.getNameFromClientsByUid(
                        this.currentBarChartData.filterValues[index][key][points[0]._index],
                        this.users
                      );
                      value = this.currentBarChartData.filterValues[index][key][points[0]._index];
                    } else if (
                      this.currentBarChartData.filterKey[key] === "with_contact" ||
                      this.currentBarChartData.filterKey[key] === "without_contact"
                    ) {
                      title = this.$h.getNameFromClientsByUid(
                        this.currentBarChartData.filterValues[index][key][points[0]._index],
                        this.contacts
                      );
                      value = this.currentBarChartData.filterValues[index][key][points[0]._index];
                    }
                    option.active = true;
                    option.select.model.push({
                      label: title,
                      value: value,
                    });
                  } else {
                    this.currentBarChartData.filterValues[index].forEach((val) => {
                      for (let i = 0; i < option.select.model.length; i += 1) {
                        if (option.select.model[i].value === val) {
                          option.select.model.splice(i, 1);
                          if (option.select.model.length < 1) {
                            option.active = false;
                          }
                        }
                      }
                    });
                  }
                  // check and remove value if value is already in filter array
                } else if (!option.select && option.switch && option.switch.name === value) {
                  if (!option.active) {
                    if (this.currentBarChartData.filterValues[index][key] !== "") {
                      option.active = true;
                      option.switch.model = this.currentBarChartData.filterValues[index][key];
                    } else {
                      option.active = false;
                    }
                  } else {
                    option.active = false;
                  }
                }
              });
            } else {
              if (!option.select && option.switch && option.switch.name === this.currentBarChartData.filterKey) {
                if (!option.active) {
                  option.active = true;
                  option.switch.model = this.currentBarChartData.filterValues[index];
                } else {
                  option.active = false;
                }
              }
            }
          });
        }
        // do filter request
        const params = {
          search: this.searchQuery,
          page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.page,
          per_page: this.teamsSettings[this.$route.params.team].listsSettings.projects.pagination.perPage,
          order_col: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.col,
          order_dir: this.teamsSettings[this.$route.params.team].listsSettings.projects.order.dir,
          with_pageviewed: filterPages,
        };
        this.getProjects(params, true);
      }
    },
    showDeleteModal(uid) {
      this.currentDeleteUid = uid;
      this.$modal.show("delete-projects-modal");
    },
    deleteProjectModalHandler(uid) {
      const vm = this;
      this.currentDeleteUid = null;
      this.$modal.hide("delete-projects-modal");
      this.deleteProject({ project: uid })
        .then(() => {
          const p = {
            search: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.search,
            page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.page,
            per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.perPage,
            order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.col,
            order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.dir,
            with_pageviewed:
              vm.teamsSettings[vm.$route.params.team].listsSettings.projects.filter.with_pageviewed.model,
          };
          let params = vm.setFilterRequestObjectFromStore("projects", p);
          vm.getProjectList({
            setListsSettings: true,
            params,
          });
          this.loadStats(params);
          this.$snotify.success(this.$t("toasts.deleteProject.success"), this.$t("toasts.success"), {
            icon: this.$successIcon,
          });
        })
        .catch((err) => {
          if (err.response && !err.response.data.error.custom) {
            this.$snotify.error(this.$t("toasts.deleteProject.error"), this.$t("toasts.error"), {
              timeout: 0,
              icon: this.$errorIcon,
            });
          }
        });
    },
    showDuplicateModal(uid) {
      this.duplicateUid = uid;
      this.duplicateTitle = this.$h.getNameFromProjectsByUid(uid, this.projects);
      const params = {
        per_page: "1000",
        order_col: "last_name",
        order_dir: "ASC",
        search: "",
      };
      this.getClientList({ params, customLoading: true }).then((res) => {
        this.clientOptions = res;
        this.$modal.show("duplicate-project");
      });
    },
    duplicateModalClosed() {
      this.duplicateUid = null;
      this.duplicateTitle = "";
      this.duplicateClients = [];
      this.duplicateFiles = true;
    },
    duplicateThisProject() {
      const vm = this;
      vm.$validator.validateAll().then((result) => {
        if (result) {
          vm.$modal.hide("duplicate-project");
          let clients = [];
          Object.keys(vm.duplicateClients).forEach((key) => {
            clients.push(vm.duplicateClients[key].uid);
          });
          vm.duplicateProject({
            params: { project: vm.duplicateUid },
            data: {
              clients: clients,
              with_files: vm.duplicateFiles,
              title: vm.duplicateTitle,
            },
          })
            .then(() => {
              let p = {
                search: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.search,
                page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.page,
                per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.perPage,
                order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.col,
                order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.dir,
                with_pageviewed:
                  vm.teamsSettings[vm.$route.params.team].listsSettings.projects.filter.with_pageviewed.model,
              };
              let params = vm.setFilterRequestObjectFromStore("projects", p);
              vm.getProjectList({
                setListsSettings: true,
                params,
              });
              this.loadStats(params);
              this.$snotify.success(this.$t("toasts.duplicateProject.success"), this.$t("toasts.success"), {
                icon: this.$successIcon,
              });
            })
            .catch((err) => {
              vm.$modal.hide("duplicate-project");
              if (err.response && !err.response.data.error.custom) {
                this.$snotify.error(this.$t("toasts.duplicateProject.error"), this.$t("toasts.error"), {
                  timeout: 0,
                  icon: this.$errorIcon,
                });
              }
            });
        }
      });
    },
    setCustomLabel(client) {
      const name = this.$h.formatName(client);
      const email = "<" + client.email + ">";
      const res = name + " " + email;
      return `${res}`;
    },
    loadClients(query) {
      if (query.length > 1) {
        this.isLoading = true;
        const params = {
          per_page: "1000",
          order_col: "last_name",
          order_dir: "ASC",
          search: query,
        };
        this.getClientList({ params, customLoading: true }).then((res) => {
          this.clientOptions = res;
          this.isLoading = false;
        });
      } else if (query.length === 0) {
        this.isLoading = true;
        const params = {
          per_page: "1000",
          order_col: "last_name",
          order_dir: "ASC",
          search: "",
        };
        this.getClientList({ params, customLoading: true }).then((res) => {
          this.clientOptions = res;
          this.isLoading = false;
        });
      }
    },
    setUsersDropdownOptions() {
      let userOptions = [];
      if (this.users) {
        this.users.forEach((user) => {
          const name = this.$h.formatName(user);
          userOptions.push({ label: name, value: user.uid });
        });
      }
      this.filterDropdown[0].options.forEach((option) => {
        if (option.select) {
          if (option.select.name === "with_user" || option.select.name === "without_user") {
            option.select.options = userOptions;
          }
        }
      });
    },
    setContactsDropdownOptions() {
      let contactOptions = [];
      if (this.contacts) {
        this.contacts.forEach((contact) => {
          const name = this.$h.formatName(contact);
          contactOptions.push({ label: name, value: contact.uid });
        });
      }
      this.filterDropdown[0].options.forEach((option) => {
        if (option.select) {
          if (option.select.name === "with_contact" || option.select.name === "without_contact") {
            option.select.options = contactOptions;
          }
        }
      });
    },
    selectFilterPreset(preset) {
      let vm = this;
      let params = {
        search: vm.searchQuery,
        page: 1,
        per_page: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.pagination.perPage,
        order_col: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.col,
        order_dir: vm.teamsSettings[vm.$route.params.team].listsSettings.projects.order.dir,
      };
      Object.assign(params, preset.value);
      if (params.time_relative && params.time_from && params.time_to) {
        params.time_from = "";
        params.time_to = "";
      }
      if (params.time_from && params.time_to && !params.time_relative) params.time_relative = "custom";
      vm.getProjectList({
        setListsSettings: true,
        params,
      }).then(() => {
        vm.setCurrentFilterPresetHasChanged({ list: "projects", state: false });
        vm.setCurrentFilterPreset({ list: "projects", filterPreset: preset });
        this.$modal.hide("showFilterPresets");
        vm.initSort("projects");
        vm.initShowAs("projects");
        vm.initFilters("projects");
      });
      this.loadStats(params);
    },
    setSentStatus(project) {
      const vm = this;
      vm.setProjectPosted({
        params: { project: project },
      })
        .then(() => {
          this.$snotify.success(this.$t("toasts.setSentStatus.success"), this.$t("toasts.success"), {
            icon: this.$successIcon,
          });
          vm.loadPageData();
        })
        .catch(() => {
          this.$snotify.error(this.$t("toasts.setSentStatus.error"), this.$t("toasts.error"), {
            timeout: 0,
            icon: this.$errorIcon,
          });
        });
    },
  },
  computed: {
    filteredProjectStatusCharts() {
      return this.projectsStats.status ? this.projectsStats.status : {};
    },
    ...mapGetters({
      teamsSettings: "global_get_teams_settings",
      teams: "auth_get_teams",
      projects: "projects_get_project_list",
      projectsStats: "projects_get_projects_stats",
      projectsStati: "projects_get_projects_stati",
      projectsCustomFields: "global_get_projects_custom_fields",
      currentProject: "projects_get_current_project",
      templates: "templates_get_templates",
      users: "user_get_users",
      contacts: "teams_get_contacts",
      builderSession: "projects_get_builder_session",
      clients: "clients_get_client_list",
      filterPresets: "filterpresets_get_filterpresets",
      teamTags: "teams_get_tags",
      showTips: "global_get_show_tips",
      showAlways: "global_get_show_tips_icon",
    }),
  },
};
</script>
